import { z } from 'zod';

import {
  createEntityAdapterSchema,
  findDuplicateIds,
  findDuplicateNames,
} from '../../base';
import { FlowSchema } from './flow.schema';
import { findDuplicateLabels } from './flow.utils';

// Flows collection schema
export const FlowsSchema = z
  .object({
    flows: createEntityAdapterSchema(FlowSchema),
  })
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  .superRefine((data, ctx) => {
    const flows = Object.values(data.flows.entities);

    const duplicateNames = findDuplicateNames(flows);
    const duplicateIds = findDuplicateIds(flows);
    for (const duplicate of duplicateNames) {
      ctx.addIssue({
        path: ['flows', 'entities', duplicate.id, 'name'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate name',
      });
    }
    for (const duplicate of duplicateIds) {
      ctx.addIssue({
        path: ['flows', 'entities', duplicate.id, 'id'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate ID',
      });
    }

    // Function step names must be globally unique across all flows because
    // llemur resolves them by name at runtime, not scoped per-flow.
    // Carry flowId + stepId through so we never re-resolve the owner by step ID
    // (step IDs are only unique within a flow, not globally).
    const allFunctionSteps = flows.flatMap(flow =>
      Object.values(flow.steps.entities)
        .filter(
          (s): s is NonNullable<typeof s> =>
            s !== null && s !== undefined && s.type === 'function_step',
        )
        .map(step => ({
          id: `${flow.id}:${step.id}`, // composite key — globally unique
          flowId: flow.id,
          stepId: step.id,
          name: step.name,
        })),
    );
    const duplicateFunctionStepNames = findDuplicateNames(allFunctionSteps);
    for (const duplicate of duplicateFunctionStepNames) {
      ctx.addIssue({
        path: [
          'flows',
          'entities',
          duplicate.flowId,
          'steps',
          'entities',
          duplicate.stepId,
          'name',
        ],
        code: z.ZodIssueCode.custom,
        message: 'Function step name must be unique across all flows',
      });
    }

    for (const flow of flows) {
      const steps = Object.values(flow.steps.entities);

      const duplicateStepNames = findDuplicateNames(steps);
      const duplicateStepIds = findDuplicateIds(steps);

      for (const duplicate of duplicateStepNames) {
        ctx.addIssue({
          path: [
            'flows',
            'entities',
            flow.id,
            'steps',
            'entities',
            duplicate.id,
            'name',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate name',
        });
      }

      for (const duplicate of duplicateStepIds) {
        ctx.addIssue({
          path: [
            'flows',
            'entities',
            flow.id,
            'steps',
            'entities',
            duplicate.id,
            'id',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate ID',
        });
      }

      const fns = Object.values(flow.transitionFunctions.entities).filter(
        fn => !fn.archived,
      );
      const duplicateNames = findDuplicateNames(fns);
      const duplicateIds = findDuplicateIds(fns);
      for (const duplicate of duplicateNames) {
        ctx.addIssue({
          path: [
            'flows',
            'entities',
            flow.id,
            'transitionFunctions',
            'entities',
            duplicate.id,
            'name',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate name',
        });
      }
      for (const duplicate of duplicateIds) {
        ctx.addIssue({
          path: [
            'flows',
            'entities',
            flow.id,
            'transitionFunctions',
            'entities',
            duplicate.id,
            'id',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate ID',
        });
      }

      for (const fn of fns) {
        const parameters = Object.values(fn.parameters.entities);
        const duplicateNames = findDuplicateNames(parameters);
        const duplicateIds = findDuplicateIds(parameters);
        for (const duplicate of duplicateNames) {
          ctx.addIssue({
            path: [
              'flows',
              'entities',
              flow.id,
              'transitionFunctions',
              'entities',
              fn.id,
              'parameters',
              'entities',
              duplicate.id,
              'name',
            ],
            code: z.ZodIssueCode.custom,
            message: 'Duplicate name',
          });
        }
        for (const duplicate of duplicateIds) {
          ctx.addIssue({
            path: [
              'flows',
              'entities',
              flow.id,
              'transitionFunctions',
              'entities',
              fn.id,
              'parameters',
              'entities',
              duplicate.id,
              'id',
            ],
            code: z.ZodIssueCode.custom,
            message: 'Duplicate ID',
          });
        }
      }

      // Validate condition labels are unique within each step
      for (const step of steps) {
        // Only default_step and function_step have conditions
        if (step.type !== 'default_step' && step.type !== 'function_step') {
          continue;
        }

        const conditions = step.conditions || [];
        if (conditions.length === 0) {
          continue;
        }

        const duplicateLabels = findDuplicateLabels(conditions);
        for (const duplicate of duplicateLabels) {
          const duplicateIndex = conditions.findIndex(
            c => c.id === duplicate.id,
          );
          if (duplicateIndex === -1) {
            continue;
          }

          // Extract label safely - findDuplicateLabels only returns items with labels
          const label = duplicate.config.value?.details?.label ?? 'unknown';

          ctx.addIssue({
            path: [
              'flows',
              'entities',
              flow.id,
              'steps',
              'entities',
              step.id,
              'conditions',
              String(duplicateIndex),
              'config',
              'value',
              'details',
              'label',
            ],
            code: z.ZodIssueCode.custom,
            message: `Duplicate condition label "${label}"`,
          });
        }
      }
    }
  });

export type FlowsState = z.infer<typeof FlowsSchema>;
